import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Tasks from "../tasks/tasks";
import FollowupsScheduleDashboard from "../followup-schedule/followups-schedule-dashboard";
import OverviewReport from "./overview-report";
import { useAuth } from "src/context/auth";
import { useDashboardData } from "src/context/dashboard-data";
import { useTasksData } from "src/context/tasks-data";
import {
  handleSendMessage,
  isWithinTwoWeeks,
  sortByDateAndPriority,
  filterActiveTasks,
  filterActiveFollowups,
} from "src/utilities";
import { useNotifications } from "@toolpad/core/useNotifications";
import { IFollowupSchedule } from "src/interface/IFollowupSchedule";
import { LinearProgress } from "@mui/material";

// ==================== تنظیمات اولیه ====================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function TabPanel() {
  const [value, setValue] = React.useState(0);
  const { user } = useAuth();
  const notifications = useNotifications();
  const currentPath = window.location.pathname;

  // ==================== استفاده از Context ها ====================
  const {
    serviceGroups: dataServiceGroups,
    services: dataServices,
    isLoadingServiceGroups,
    isLoadingServices,
  } = useDashboardData();

  const {
    tasks: dataTasks,
    followupsSchedule: dataFollowupsSchedule,
    isLoadingTasks,
    isLoadingFollowupsSchedule: isFollowUpLoading,
    refetchTasks,
    refetchFollowupsSchedule,
    errorTasks,
    errorFollowupsSchedule,
  } = useTasksData();

  // ==================== مدیریت خطاها ====================
  React.useEffect(() => {
    if (errorTasks) {
      handleSendMessage(
        notifications,
        `خطا در دریافت اطلاعات: ${errorTasks}`,
        "error"
      );
    }
  }, [errorTasks, notifications]);

  React.useEffect(() => {
    if (errorFollowupsSchedule) {
      handleSendMessage(
        notifications,
        `خطا در دریافت اطلاعات: ${errorFollowupsSchedule}`,
        "error"
      );
    }
  }, [errorFollowupsSchedule, notifications]);

  // ==================== پردازش داده‌ها ====================
  // پردازش داده‌های کارها
  const tasksList = React.useMemo(() => {
    if (!dataTasks) return [];

    const filteredData = dataTasks.filter(
      (task) => task.dueDate && isWithinTwoWeeks(task.dueDate)
    );
    const sortedData = filteredData.sort((a, b) =>
      sortByDateAndPriority(a, b, "dueDate", "priority")
    );
    const activeTasks = filterActiveTasks(sortedData);

    return currentPath === "/"
      ? activeTasks.filter((t) => t.user?.userName === user?.userName)
      : activeTasks;
  }, [dataTasks, currentPath, user?.userName]);

  // پردازش داده‌های پیگیری‌ها
  const followupsScheduleList = React.useMemo(() => {
    if (!dataFollowupsSchedule) return [];

    const rawData = Array.isArray(dataFollowupsSchedule)
      ? dataFollowupsSchedule
      : (dataFollowupsSchedule as any)?.data || [];
    const filteredData = rawData.filter(
      (f: IFollowupSchedule) => f.dueOn && isWithinTwoWeeks(f.dueOn.toString())
    );

    const sortedData = filteredData.sort(
      (a: IFollowupSchedule, b: IFollowupSchedule) =>
        sortByDateAndPriority(a, b, "dueOn", "priroty")
    );

    const activeFollowups = filterActiveFollowups(sortedData);

    return currentPath === "/"
      ? activeFollowups.filter((f) => f.creator?.userName === user?.userName)
      : activeFollowups;
  }, [dataFollowupsSchedule, currentPath, user?.userName]);

  // محاسبه شمارنده‌ها
  const counters = React.useMemo(() => {
    const filteredFollowups = followupsScheduleList.filter(
      (f) => f.parentRef !== null
    );

    const agreementFollowups = filteredFollowups.filter(
      (f) => f.agreementRef !== null
    );
    const internalFollowups = filteredFollowups.filter(
      (f) => f.agreementRef === null
    );

    // محاسبه کارهای شخصی
    let personalTasks = 0;
    if (dataServiceGroups && dataServices) {
      const groupedServiceRefs = new Set();
      dataServiceGroups.forEach((group) => {
        const groupServices = dataServices.filter(
          (s) => s.serviceGroupId === group.id
        );
        groupServices.forEach((service) => groupedServiceRefs.add(service.id));
      });

      personalTasks = internalFollowups.filter(
        (f) => !groupedServiceRefs.has(f.serviceRef)
      ).length;
    }

    return {
      tasks: tasksList.length,
      agreementFollowups: agreementFollowups.length,
      internalFollowups: internalFollowups.length,
      personalTasks,
    };
  }, [followupsScheduleList, dataServiceGroups, dataServices, tasksList]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab
            label={`پیگیری مشتریان (${counters.agreementFollowups})`}
            {...a11yProps(0)}
          />
          <Tab
            label={`پیگیری های داخلی (${counters.personalTasks})`}
            {...a11yProps(1)}
          />
          <Tab label={`وظایف (${counters.tasks})`} {...a11yProps(2)} />
          {/* <Tab label="گزارش کلی از برنامه" {...a11yProps(3)} /> */}
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        {(isLoadingTasks ||
          isFollowUpLoading ||
          isLoadingServiceGroups ||
          isLoadingServices) && <LinearProgress />}
        <FollowupsScheduleDashboard
          followupType="Agreement"
          isLoading={false}
          refetch={refetchFollowupsSchedule}
          rows={
            followupsScheduleList?.filter((f) => f.parentRef !== null) ?? []
          }
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <FollowupsScheduleDashboard
          followupType="Customer"
          isLoading={false}
          refetch={refetchFollowupsSchedule}
          rows={
            followupsScheduleList?.filter((f) => f.parentRef !== null) ?? []
          }
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Tasks
          isLoading={isLoadingTasks}
          refetch={refetchTasks}
          rows={tasksList}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <OverviewReport
          users={[]} // TODO: اضافه کردن داده‌های کاربران
          tasks={dataTasks || []}
          followups={dataFollowupsSchedule || []}
          agreements={[]} // TODO: اضافه کردن داده‌های قراردادها
          transactions={[]} // TODO: اضافه کردن داده‌های تراکنش‌ها
          services={dataServices || []}
          systems={[]} // TODO: اضافه کردن داده‌های سامانه‌ها
          isLoading={isLoadingTasks || isFollowUpLoading || isLoadingServices}
        />
      </CustomTabPanel>
    </Box>
  );
}
